import { useEffect, useMemo, useState } from 'react';

import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import BasicPagination from '../../components/DataTable/BasicPagination';
import {
  PrinterBranchEmpty,
  PrinterFilters,
  PrinterHeader,
  PrinterStats,
  PrinterStatusFilter,
  RestaurantPrinterFormModal,
  RestaurantPrinterList,
} from '../../features/restaurant/printers';
import useRestaurantPrinters from '../../hooks/restaurant/useRestaurantPrinters';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../layout/WorkspaceLayout';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import { RestaurantPrinterConnectionType, RestaurantPrinterType } from '../../types/restaurant/RestaurantPrinterTypes';

const RestaurantPrinters = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PrinterStatusFilter>('ALL');
  const [printerType, setPrinterType] = useState<RestaurantPrinterType | 'ALL'>('ALL');
  const [connectionType, setConnectionType] = useState<RestaurantPrinterConnectionType | 'ALL'>('ALL');
  const [enabled, setEnabled] = useState<'ALL' | 'ENABLED' | 'DISABLED'>('ALL');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const debouncedSearch = useDebouncedValue(search, 350);

  const printers = useRestaurantPrinters(branchId);

  useEffect(() => {
    const loadBranches = async () => {
      setLoadingBranches(true);
      setBranchError('');

      try {
        const data = canPickBranch
          ? await BranchService.fetchDropdown()
          : [await BranchService.fetchMyBranch()];

        setBranches(data);

        if (data.length) {
          setBranchId(data[0].id);
        }
      } catch (error: any) {
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        );
      } finally {
        setLoadingBranches(false);
      }
    };

    loadBranches();
  }, [canPickBranch]);

  const filters = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    active: status === 'ALL' ? undefined : status === 'ACTIVE',
    printerType: printerType === 'ALL' ? undefined : printerType,
    connectionType: connectionType === 'ALL' ? undefined : connectionType,
    enabled: enabled === 'ALL' ? undefined : enabled === 'ENABLED',
    page,
    size,
  }), [connectionType, debouncedSearch, enabled, page, printerType, size, status]);

  useEffect(() => {
    if (branchId) void printers.load(filters);
  }, [branchId, filters]);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <PrinterHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          onCreate={printers.openCreate}
          onBranchChange={(id) => {
            setSearch('');
            setStatus('ALL');
            setPrinterType('ALL');
            setConnectionType('ALL');
            setEnabled('ALL');
            setPage(0);
            setBranchId(id);
          }}
        />

        {!loadingBranches && !branchError && branches.length === 0 ? (
          <PrinterBranchEmpty />
        ) : branchId ? (
          <>
            <PrinterStats printers={printers.list} />

            <PrinterFilters
              search={search}
              status={status}
              printerType={printerType}
              connectionType={connectionType}
              enabled={enabled}
              onSearchChange={(value) => { setSearch(value); setPage(0); }}
              onStatusChange={(value) => { setStatus(value); setPage(0); }}
              onPrinterTypeChange={(value) => { setPrinterType(value); setPage(0); }}
              onConnectionTypeChange={(value) => { setConnectionType(value); setPage(0); }}
              onEnabledChange={(value) => { setEnabled(value); setPage(0); }}
            />

            <RestaurantPrinterList
              printers={printers.list}
              loading={printers.loading}
              onEdit={printers.edit}
              onDeactivate={printers.deactivate}
            />
            {printers.page && (
              <BasicPagination
                currentPage={printers.page.number}
                totalPages={printers.page.totalPages}
                totalElements={printers.page.totalElements}
                size={printers.page.size}
                first={printers.page.number === 0}
                last={printers.page.number >= printers.page.totalPages - 1}
                onPageChange={setPage}
                onSizeChange={(value) => { setSize(value); setPage(0); }}
              />
            )}
          </>
        ) : null}
      </SectionWrapper>

      <RestaurantPrinterFormModal
        isOpen={printers.modalOpen}
        form={printers.form}
        saving={printers.saving}
        onClose={printers.closeModal}
        onChange={printers.updateField}
        onSave={printers.save}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantPrinters;
